/**
 * Pending Review Page
 *
 * Manual operator identification review interface for managers.
 * Displays video frames requiring human verification when
 * face recognition confidence is below threshold.
 */

import { useState, useCallback } from 'react';
import {
  ScanFace,
  Video,
  Clock,
  Camera,
  UserCheck,
  SkipForward,
  AlertCircle,
  CheckCircle2,
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Users,
  Eye,
  Sparkles,
} from 'lucide-react';
import {
  usePendingReviews,
  usePendingReviewDetail,
  useResolvePendingReview,
  type PendingIdentification,
  type PendingCandidate,
} from '../../hooks/usePendingReviews';
import { useOperatorLookup } from '../../hooks/useOperators';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';

// Format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// Confidence level styling
function getConfidenceStyle(similarity: number): {
  bg: string;
  text: string;
  bar: string;
  label: string;
} {
  if (similarity >= 70) {
    return {
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      bar: 'bg-gradient-to-r from-amber-400 to-amber-500',
      label: 'Medium',
    };
  }
  if (similarity >= 40) {
    return {
      bg: 'bg-orange-50',
      text: 'text-orange-700',
      bar: 'bg-gradient-to-r from-orange-400 to-orange-500',
      label: 'Low',
    };
  }
  return {
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    bar: 'bg-gradient-to-r from-rose-400 to-rose-500',
    label: 'Very Low',
  };
}

// Confidence Bar Component
function ConfidenceBar({ similarity }: { similarity: number }) {
  const style = getConfidenceStyle(similarity);

  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full transition-all duration-500 ${style.bar}`}
          style={{ width: `${similarity}%` }}
        />
      </div>
      <span className={`text-xs font-semibold tabular-nums ${style.text}`}>
        {Math.round(similarity)}%
      </span>
    </div>
  );
}

// Candidate Card Component
function CandidateCard({
  candidate,
  isSelected,
  onSelect,
}: {
  candidate: PendingCandidate;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={`group relative flex w-full items-center gap-3 rounded-xl border-2 p-3 text-left transition-all duration-200 ${
        isSelected
          ? 'border-guild-green bg-guild-mint/20 ring-2 ring-guild-green/20'
          : 'border-gray-100 bg-white hover:border-guild-green/30 hover:bg-gray-50/50'
      }`}
    >
      {/* Selection indicator */}
      <div
        className={`absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full transition-all duration-200 ${
          isSelected
            ? 'scale-100 bg-guild-green'
            : 'scale-0 bg-gray-300'
        }`}
      >
        <CheckCircle2 size={14} className="text-white" strokeWidth={2.5} />
      </div>

      {/* Photo */}
      <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
        {candidate.photoUrl ? (
          <img
            src={candidate.photoUrl}
            alt={candidate.operatorName}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Users size={20} className="text-gray-400" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900">
          {candidate.operatorName}
        </p>
        <div className="mt-1">
          <ConfidenceBar similarity={candidate.similarity} />
        </div>
      </div>
    </button>
  );
}

// Review Card Component
function ReviewCard({
  review,
  onReview,
}: {
  review: PendingIdentification;
  onReview: (review: PendingIdentification) => void;
}) {
  const topCandidate = review.topCandidates[0];
  const hasNoMatch = !topCandidate || topCandidate.similarity < 30;

  return (
    <div className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:border-guild-green/20 hover:shadow-lg hover:shadow-guild-green/5">
      {/* Status indicator */}
      <div className="absolute right-3 top-3 z-10">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/90 px-2.5 py-1 text-xs font-semibold text-white shadow-lg backdrop-blur-sm">
          <ScanFace size={12} strokeWidth={2.5} />
          Needs Review
        </span>
      </div>

      {/* Frame preview */}
      <div className="relative aspect-video overflow-hidden bg-gray-900">
        {review.frameUrl ? (
          <img
            src={review.frameUrl}
            alt="Video frame"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Video size={48} className="text-gray-600" />
          </div>
        )}

        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* Camera info overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex items-center gap-2 text-white/90">
            <Camera size={14} strokeWidth={2} />
            <span className="text-sm font-medium">{review.cameraName}</span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-white/70">
            <Clock size={12} strokeWidth={2} />
            <span className="text-xs">{formatRelativeTime(review.capturedAt)}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Top match section */}
        {hasNoMatch ? (
          <div className="flex items-center gap-3 rounded-xl bg-gray-50 p-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-200">
              <AlertCircle size={20} className="text-gray-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">No confident match</p>
              <p className="text-xs text-gray-500">Manual selection required</p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Best Match
              </span>
              {review.topCandidates.length > 1 && (
                <span className="text-xs text-gray-400">
                  +{review.topCandidates.length - 1} more
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/50 p-3">
              <div className="relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg bg-gray-200">
                {topCandidate.photoUrl ? (
                  <img
                    src={topCandidate.photoUrl}
                    alt={topCandidate.operatorName}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <Users size={16} className="text-gray-400" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">
                  {topCandidate.operatorName}
                </p>
                <ConfidenceBar similarity={topCandidate.similarity} />
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => onReview(review)}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-guild-green px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-guild-green/25 transition-all hover:bg-guild-green/90 hover:shadow-xl active:scale-[0.98]"
          >
            <Eye size={16} strokeWidth={2.5} />
            Review
          </button>
        </div>
      </div>
    </div>
  );
}

// Review Modal Component
function ReviewModal({
  reviewId,
  onClose,
  onResolved,
}: {
  reviewId: string;
  onClose: () => void;
  onResolved: () => void;
}) {
  const { data: review, isLoading } = usePendingReviewDetail(reviewId);
  const { data: operators } = useOperatorLookup();
  const resolveMutation = useResolvePendingReview();

  const [selectedOperatorId, setSelectedOperatorId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [showAllOperators, setShowAllOperators] = useState(false);

  const handleSelectOperator = useCallback(async () => {
    if (!selectedOperatorId) return;

    try {
      await resolveMutation.mutateAsync({
        id: reviewId,
        action: 'select_operator',
        operatorId: selectedOperatorId,
        notes: notes || undefined,
      });
      onResolved();
    } catch (error) {
      console.error('Failed to resolve identification:', error);
    }
  }, [reviewId, selectedOperatorId, notes, resolveMutation, onResolved]);

  const handleSkip = useCallback(async () => {
    try {
      await resolveMutation.mutateAsync({
        id: reviewId,
        action: 'skip',
        notes: notes || undefined,
      });
      onResolved();
    } catch (error) {
      console.error('Failed to skip identification:', error);
    }
  }, [reviewId, notes, resolveMutation, onResolved]);

  const isSubmitting = resolveMutation.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && !isSubmitting && onClose()}
    >
      <div className="animate-in fade-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl duration-200">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-guild-green to-guild-green/80 shadow-lg shadow-guild-green/20">
              <ScanFace size={20} className="text-white" strokeWidth={2.5} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Identify Operator</h2>
              <p className="text-sm text-gray-500">
                Select the operator visible in this video
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-sm text-gray-500">Loading review details...</p>
            </div>
          ) : review ? (
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Left: Video frame */}
              <div>
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Video Frame
                </h3>
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-900">
                  <div className="relative aspect-video">
                    {review.frameUrl ? (
                      <img
                        src={review.frameUrl}
                        alt="Video frame"
                        className="h-full w-full object-contain"
                      />
                    ) : review.videoUrl ? (
                      <video
                        src={review.videoUrl}
                        controls
                        className="h-full w-full object-contain"
                        preload="metadata"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Video size={48} className="text-gray-600" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <Camera size={14} />
                    <span>{review.cameraName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={14} />
                    <span>{formatRelativeTime(review.capturedAt)}</span>
                  </div>
                </div>
              </div>

              {/* Right: Candidate selection */}
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Select Operator
                  </h3>
                  {review.topCandidates.length > 0 && (
                    <button
                      onClick={() => setShowAllOperators(!showAllOperators)}
                      className="flex items-center gap-1 text-xs font-medium text-guild-green hover:underline"
                    >
                      {showAllOperators ? 'Show suggestions' : 'Show all operators'}
                    </button>
                  )}
                </div>

                {showAllOperators ? (
                  /* All operators dropdown */
                  <div className="space-y-3">
                    <select
                      value={selectedOperatorId ?? ''}
                      onChange={(e) => setSelectedOperatorId(e.target.value || null)}
                      className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
                    >
                      <option value="">Select an operator...</option>
                      {operators?.map((op) => (
                        <option key={op.id} value={op.id}>
                          {op.fullName}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500">
                      Showing all {operators?.length ?? 0} operators in your organization
                    </p>
                  </div>
                ) : review.topCandidates.length > 0 ? (
                  /* Suggested candidates */
                  <div className="space-y-2">
                    {review.topCandidates.map((candidate: PendingCandidate) => (
                      <CandidateCard
                        key={candidate.operatorId}
                        candidate={candidate}
                        isSelected={selectedOperatorId === candidate.operatorId}
                        onSelect={() => setSelectedOperatorId(candidate.operatorId)}
                      />
                    ))}
                  </div>
                ) : (
                  /* No candidates - show dropdown directly */
                  <div className="space-y-3">
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle size={20} className="mt-0.5 text-amber-600" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">
                            No matching faces found
                          </p>
                          <p className="mt-1 text-xs text-amber-700">
                            The system couldn't identify the operator. Please select manually.
                          </p>
                        </div>
                      </div>
                    </div>
                    <select
                      value={selectedOperatorId ?? ''}
                      onChange={(e) => setSelectedOperatorId(e.target.value || null)}
                      className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
                    >
                      <option value="">Select an operator...</option>
                      {operators?.map((op) => (
                        <option key={op.id} value={op.id}>
                          {op.fullName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Notes */}
                <div className="mt-4">
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Notes (optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any relevant notes..."
                    rows={2}
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <AlertCircle size={48} className="text-gray-300" />
              <p className="mt-4 text-sm text-gray-500">Failed to load review details</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50/50 px-6 py-4">
          <button
            onClick={handleSkip}
            disabled={isSubmitting}
            className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <SkipForward size={16} />
            )}
            Skip (No Operator)
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-70"
            >
              Cancel
            </button>
            <button
              onClick={handleSelectOperator}
              disabled={!selectedOperatorId || isSubmitting}
              className="flex items-center gap-2 rounded-xl bg-guild-green px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-guild-green/25 transition-all hover:bg-guild-green/90 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-70 disabled:shadow-none"
            >
              {isSubmitting ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <UserCheck size={16} />
              )}
              Confirm Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Page Component
export default function PendingReviewPage() {
  useDocumentTitle('Pending Review');
  const [page, setPage] = useState(1);
  const { data: reviewsData, isLoading, error } = usePendingReviews({ page, limit: 12 });
  const [activeReviewId, setActiveReviewId] = useState<string | null>(null);

  const reviews = reviewsData?.data ?? [];
  const pagination = reviewsData?.pagination;
  const totalPages = pagination?.totalPages ?? 1;

  const handleReview = useCallback((review: PendingIdentification) => {
    setActiveReviewId(review.id);
  }, []);

  const handleResolved = useCallback(() => {
    setActiveReviewId(null);
  }, []);

  const handleCloseModal = useCallback(() => {
    setActiveReviewId(null);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-guild-green to-guild-green/80 shadow-lg shadow-guild-green/20">
            <ScanFace size={24} className="text-white" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Pending Reviews
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Video clips requiring manual operator identification
            </p>
          </div>
        </div>

        {pagination && pagination.total > 0 && (
          <div className="flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-2">
            <Sparkles size={16} className="text-amber-600" />
            <span className="text-sm font-medium text-amber-700">
              {pagination.total} pending {pagination.total === 1 ? 'review' : 'reviews'}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-24">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-sm text-gray-500">Loading pending reviews...</p>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-24">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-50">
            <AlertCircle size={32} className="text-rose-500" />
          </div>
          <p className="mt-4 text-sm font-medium text-gray-900">Failed to load reviews</p>
          <p className="mt-1 text-sm text-gray-500">Please try refreshing the page</p>
        </div>
      ) : reviews.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24">
          <div className="relative">
            <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-emerald-50 to-emerald-100">
              <CheckCircle2 size={40} className="text-emerald-500" />
            </div>
            <div className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 shadow-lg">
              <Sparkles size={16} className="text-white" />
            </div>
          </div>
          <h3 className="mt-6 text-lg font-semibold text-gray-900">All caught up!</h3>
          <p className="mt-1 max-w-sm text-center text-sm text-gray-500">
            There are no video clips pending operator identification. New reviews will appear here
            automatically.
          </p>
        </div>
      ) : (
        <>
          {/* Grid */}
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {reviews.map((review) => (
              <ReviewCard key={review.id} review={review} onReview={handleReview} />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-between rounded-2xl border border-gray-100 bg-white px-6 py-4 shadow-sm">
              <p className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Review Modal */}
      {activeReviewId && (
        <ReviewModal
          reviewId={activeReviewId}
          onClose={handleCloseModal}
          onResolved={handleResolved}
        />
      )}
    </div>
  );
}
